import { request } from "@/common/api";
import { Loading } from "@/components/atoms/Loading";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MainContent } from "@/components/organisms/MainContent";
import { ExerciseList } from "@/components/molecules/ExerciseList";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { exerciseStart } from "@/common/functions/flutter";
import { ExerciseState } from "@/components/molecules/ExerciseState";
import { useRecoilValue } from "recoil";
import { ExerciseTodayBox } from "@/components/molecules/ExerciseTodayBox";
import { WeekExcerciseList } from "@/components/molecules/WeekExcerciseList";
import { CommentFloatMessage } from "@/components/atoms/CommentFloatMessage";
import { HealthSummaryResponse } from "@/common/types/healthData";
import { HealthEnterWarningItem } from "@/components/atoms/HealthEnterInfoItem";
import { ExercisePointEligibility } from "@/common/types/student";
import { loginTypeState, studentUserState } from "@/common/states/user";
import { ExerciseCategory, ExerciseSummary } from "@/common/types/video";
import {
  GET_EXERCISE_AVAILABILITY,
  GET_STUDENT_HEALTH_SUMMARY,
  GET_UNREAD_MESSAGE,
} from "@/common/querykeys";
import {
  baseExerciseCategories,
  baseWeekExerciseCategories,
} from "@/data/category";
import { EventExercise } from "@/components/molecules/EventExercise";

export const ExercisePage = () => {
  const student = useRecoilValue(studentUserState);
  const loginType = useRecoilValue(loginTypeState);
  const [exerciseModalShow, setExerciseModalShow] = useState(false);

  const { data: exerciseSummary, isPending } = useQuery({
    queryKey: ["exercise-categories"],
    queryFn: () =>
      request<ExerciseSummary>({
        method: "GET",
        url: `/exercise/summary/${student?.id}`,
      }),
  });

  const { data: hasUnread, isPending: hasUnreadFetching } = useQuery<boolean>({
    queryKey: [GET_UNREAD_MESSAGE],
    queryFn: async () => {
      return await request<boolean>({
        method: "GET",
        url: `/cheer-message/student/unread/check`,
      });
    },
    enabled: loginType === "student",
  });

  const { data: studentHealthSummary } = useQuery<HealthSummaryResponse>({
    queryKey: [GET_STUDENT_HEALTH_SUMMARY],
    queryFn: async () => {
      return await request<any>({
        method: "GET",
        url: `/student-health/summary/${student.id}`,
      });
    },
    enabled: !!student.id,
  });

  // 학생이 운동할 때 포인트 지급 가능 여부 조회 (🧑‍🎓)
  const { data: exercisePointEligibility } = useQuery<ExercisePointEligibility>(
    {
      queryKey: [GET_EXERCISE_AVAILABILITY],
      queryFn: async () => {
        const res = await request<ExercisePointEligibility[]>({
          method: "GET",
          url: `/student/video-point-eligibility/${student.id}`,
        });

        return res[0];
      },
      enabled: loginType === "student",
    }
  );

  const exerciseCategories: ExerciseCategory[] = baseExerciseCategories.map(
    (category) => {
      if (!exerciseSummary?.categoryCounts) {
        return {
          ...category,
        };
      }

      const foundCount = exerciseSummary?.categoryCounts.find(
        (countItem) => countItem.category === category.category
      );

      return {
        ...category,
        count: foundCount ? foundCount.count : 0,
      };
    }
  );

  const weekExerciseCategories: ExerciseCategory[] =
    baseWeekExerciseCategories.map((category) => {
      if (!exerciseSummary?.categoryCounts) {
        return {
          ...category,
        };
      }

      const foundCount = exerciseSummary?.categoryCounts.find(
        (countItem) => countItem.category === category.category
      );

      return {
        ...category,
        count: foundCount ? foundCount.count : 0,
      };
    });

  const handleExerciseConfirm = (result: boolean) => {
    if (result) {
      exerciseStart(student?.id, studentHealthSummary?.video?.videoId, true);
    } else {
    }
  };

  return (
    <>
      <MainTemplate
        nav={true}
        subPage={true}
        menuBtn={true}
        pageTitle="운동하기"
      >
        {isPending ? (
          <Loading />
        ) : (
          <MainContent
            commentFloat={hasUnread && !hasUnreadFetching ? true : false}
          >
            {studentHealthSummary?.health.healthEnterInfo ? (
              <></>
            ) : (
              <HealthEnterWarningItem />
            )}
            {/* 12주 운동 프로그램 */}
            <ExerciseTodayBox
              title={exerciseSummary?.video?.title ?? ""}
              duration={exerciseSummary?.video?.duration ?? 0}
              thumbnailUrl={exerciseSummary?.video?.thumbnailUrl ?? ""}
              isHealthEnterInfo={!studentHealthSummary?.health.healthEnterInfo}
              // onClick={() =>
              //   exerciseStart(student?.id, exerciseSummary?.video?.videoId, true)
              // }
              onClick={() => {
                // if (
                //   Number(exercisePointEligibility?.exerciseCount) === 36 ||
                //   exercisePointEligibility?.points === 50000
                // ) {
                //   setExerciseModalShow(true);
                // } else {
                //   exerciseStart(
                //     student?.id,
                //     studentHealthSummary?.video?.videoId,
                //     true
                //   );
                // }
                exerciseStart(
                  student?.id,
                  studentHealthSummary?.video?.videoId,
                  true
                );
              }}
            />

            {/* 이벤트 운동 프로그램 */}
            <EventExercise
              exercisePointEligibility={exercisePointEligibility}
              exerciseCount={Number(exercisePointEligibility?.exerciseCount)}
            />

            {/* 12주 운동 프로그램 */}
            <WeekExcerciseList
              studentId={student.id}
              weekExerciseCategories={weekExerciseCategories}
              exercisePointEligibility={exercisePointEligibility}
            />

            {/* 부위별 운동 */}
            <ExerciseList
              exerciseCategories={exerciseCategories}
              exercisePointEligibility={exercisePointEligibility}
            />

            {/* 건강 포인트 - 추후 사용 가능성 배제할 수 없기에 제거 X*/}
            {/* <ExerciseGoal
              main={false}
              exerciseData={exerciseSummary?.exercise}
              type="exercise"
            /> */}

            {/* 나의 운동 참여 현황 */}
            <ExerciseState />

            {/* 보호자 회원 응원메시지가 있는 경우 표기 */}
            {hasUnread && !hasUnreadFetching && <CommentFloatMessage />}
          </MainContent>
        )}
      </MainTemplate>
      {/* {exerciseModalShow &&
        (exercisePointEligibility &&
        exercisePointEligibility?.points === 50000 &&
        Number(exercisePointEligibility?.exerciseCount) === 36 ? (
          <ModalExerciseConfirm
            setModalShow={setExerciseModalShow}
            onConfirm={handleExerciseConfirm}
            type="all"
          />
        ) : exercisePointEligibility?.points === 50000 ? (
          <ModalExerciseConfirm
            setModalShow={setExerciseModalShow}
            onConfirm={handleExerciseConfirm}
            type="point"
          />
        ) : (
          <ModalExerciseConfirm
            setModalShow={setExerciseModalShow}
            onConfirm={handleExerciseConfirm}
            type="exercise"
          />
        ))} */}
    </>
  );
};
